import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Store, Save, Webhook } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const AdminSettings = () => {
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [settings, setSettings] = useState({
    storeName: "PlymHair",
    storeEmail: "",
    storePhone: "",
    address: "",
    currency: "GBP",
    freeShippingThreshold: "50",
    standardShippingRate: "3.99",
    expressShippingRate: "7.99",
    taxRate: "20",
    socialInstagram: "",
    socialFacebook: "",
    socialTiktok: "",
    announcementBar: "",
    announcementActive: false,
    zapierWebhookUrl: "",
    orderNotificationEmail: "tedd@plymhair.co.uk",
  });

  const update = (key: string, value: any) => setSettings((s) => ({ ...s, [key]: value }));

  const handleSave = async () => {
    // Save non-sensitive settings to localStorage
    localStorage.setItem("admin-settings", JSON.stringify(settings));

    // Save Zapier webhook URL to the database
    try {
      const now = new Date().toISOString();
      const { error } = await supabase.from("site_settings" as any).upsert(
        [
          { key: "zapier_webhook_url", value: settings.zapierWebhookUrl, updated_at: now },
          { key: "order_notification_email", value: settings.orderNotificationEmail.trim(), updated_at: now },
        ],
        { onConflict: "key" }
      );
      if (error) throw error;
      toast.success("Settings saved");
    } catch (err) {
      console.error("Failed to save settings:", err);
      toast.error("Settings saved locally but some failed to save to database.");
    }
  };

  useEffect(() => {
    // Load local settings
    const saved = localStorage.getItem("admin-settings");
    if (saved) {
      try { setSettings((prev) => ({ ...prev, ...JSON.parse(saved) })); } catch {}
    }

    const loadDbSettings = async () => {
      const { data } = await supabase
        .from("site_settings" as any)
        .select("key, value")
        .in("key", ["zapier_webhook_url", "order_notification_email"]) as any;
      const map = Object.fromEntries((data || []).map((r: any) => [r.key, r.value]));
      setSettings((prev: any) => ({
        ...prev,
        zapierWebhookUrl: map.zapier_webhook_url || prev.zapierWebhookUrl,
        orderNotificationEmail: map.order_notification_email || "tedd@plymhair.co.uk",
      }));
    };
    loadDbSettings();
  }, []);

  const inputClass = "font-body";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-bold">Settings</h1>
        <Button onClick={handleSave}><Save className="w-4 h-4 mr-1.5" />Save Settings</Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Store Info */}
        <div className="bg-card rounded-lg border border-border p-5 space-y-4">
          <h2 className="font-display font-semibold flex items-center gap-2"><Store className="w-5 h-5 text-muted-foreground" />Store Information</h2>
          <div className="space-y-2">
            <Label className="font-body">Store Name</Label>
            <Input className={inputClass} value={settings.storeName} onChange={(e) => update("storeName", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="font-body">Contact Email</Label>
            <Input className={inputClass} type="email" value={settings.storeEmail} onChange={(e) => update("storeEmail", e.target.value)} placeholder="hello@plymhair.com" />
          </div>
          <div className="space-y-2">
            <Label className="font-body">New Order Alerts Email</Label>
            <Input className={inputClass} type="email" value={settings.orderNotificationEmail} onChange={(e) => update("orderNotificationEmail", e.target.value)} placeholder="tedd@plymhair.co.uk" />
            <p className="text-xs font-body text-muted-foreground">You'll get an email here every time a customer pays for an order.</p>
          </div>
          <div className="space-y-2">
            <Label className="font-body">Phone</Label>
            <Input className={inputClass} value={settings.storePhone} onChange={(e) => update("storePhone", e.target.value)} placeholder="+44 ..." />
          </div>
          <div className="space-y-2">
            <Label className="font-body">Address</Label>
            <textarea value={settings.address} onChange={(e) => update("address", e.target.value)} placeholder="Business address…" className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm font-body resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          </div>
        </div>

        {/* Shipping & Tax */}
        <div className="bg-card rounded-lg border border-border p-5 space-y-4">
          <h2 className="font-display font-semibold">Shipping & Tax</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-body">Standard Shipping (£)</Label>
              <Input className={inputClass} type="number" step="0.01" value={settings.standardShippingRate} onChange={(e) => update("standardShippingRate", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="font-body">Express Shipping (£)</Label>
              <Input className={inputClass} type="number" step="0.01" value={settings.expressShippingRate} onChange={(e) => update("expressShippingRate", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-body">Free Shipping Over (£)</Label>
              <Input className={inputClass} type="number" step="0.01" value={settings.freeShippingThreshold} onChange={(e) => update("freeShippingThreshold", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="font-body">Tax Rate (%)</Label>
              <Input className={inputClass} type="number" step="0.1" value={settings.taxRate} onChange={(e) => update("taxRate", e.target.value)} />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-card rounded-lg border border-border p-5 space-y-4">
          <h2 className="font-display font-semibold">Social Media</h2>
          <div className="space-y-2">
            <Label className="font-body">Instagram URL</Label>
            <Input className={inputClass} value={settings.socialInstagram} onChange={(e) => update("socialInstagram", e.target.value)} placeholder="https://instagram.com/..." />
          </div>
          <div className="space-y-2">
            <Label className="font-body">Facebook URL</Label>
            <Input className={inputClass} value={settings.socialFacebook} onChange={(e) => update("socialFacebook", e.target.value)} placeholder="https://facebook.com/..." />
          </div>
          <div className="space-y-2">
            <Label className="font-body">TikTok URL</Label>
            <Input className={inputClass} value={settings.socialTiktok} onChange={(e) => update("socialTiktok", e.target.value)} placeholder="https://tiktok.com/@..." />
          </div>
        </div>

        {/* Announcement Bar */}
        <div className="bg-card rounded-lg border border-border p-5 space-y-4">
          <h2 className="font-display font-semibold">Announcement Bar</h2>
          <div className="space-y-2">
            <Label className="font-body">Message</Label>
            <Input className={inputClass} value={settings.announcementBar} onChange={(e) => update("announcementBar", e.target.value)} placeholder="Free shipping on orders over £50!" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={settings.announcementActive} onChange={(e) => update("announcementActive", e.target.checked)} className="rounded" id="announcement-active" />
            <Label htmlFor="announcement-active" className="font-body text-sm">Show announcement bar</Label>
          </div>
        </div>

        {/* Zapier Integration */}
        <div className="bg-card rounded-lg border border-border p-5 space-y-4">
          <h2 className="font-display font-semibold flex items-center gap-2"><Webhook className="w-5 h-5 text-muted-foreground" />Zapier Integration</h2>
          <p className="text-sm font-body text-muted-foreground">
            Paste your Zapier webhook URL to automatically sync new subscribers to Mailchimp (or any other service connected to your Zap).
          </p>
          <div className="space-y-2">
            <Label className="font-body">Webhook URL</Label>
            <Input className={inputClass} value={settings.zapierWebhookUrl} onChange={(e) => update("zapierWebhookUrl", e.target.value)} placeholder="https://hooks.zapier.com/hooks/catch/..." />
          </div>
          <p className="text-xs font-body text-muted-foreground">
            Data sent: email, marketing_consent, subscribed_at, source
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={!settings.zapierWebhookUrl || testingWebhook}
            onClick={async () => {
              setTestingWebhook(true);
              try {
                const { data, error } = await supabase.functions.invoke("zapier-subscriber-webhook", {
                  body: {
                    webhookUrl: settings.zapierWebhookUrl,
                    email: "test@plymhair.com",
                    marketingConsent: true,
                  },
                });
                if (error) throw error;
                toast.success("Test payload sent! Check Zapier for the request.");
              } catch (err) {
                console.error("Test webhook error:", err);
                toast.error("Failed to send test payload. Check the webhook URL.");
              } finally {
                setTestingWebhook(false);
              }
            }}
          >
            <Webhook className="w-4 h-4 mr-1.5" />
            {testingWebhook ? "Sending…" : "Send Test Payload"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
